import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Input, Select, Textarea, FileUpload, Dialog, Alert, Badge, useToast } from '@dreamtree-org/twreact-ui';
import { ScanLine, PackageCheck, Truck, CheckCircle2, AlertOctagon, Check } from 'lucide-react';
import { PageHeader, SectionCard, KeyValue, StatusBadge, Money, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { formatDate, formatINR } from '@/lib/format';
import { uploadFile } from '@/lib/uploads';
import { availableSteps, COURIERS, isLotLine, skuMatches } from '../lib/orders';
import { serialMatches, maskSerial } from '../lib/serial';

const when = (iso) => new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/** SELL-05 — one order: accept, pick the exact unit by serial scan (stock sold by quantity: SKU + count), pack evidence, courier + tracking, timeline. */
export default function OrderDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const q = useQuery('merchantOrder', { id });
  const step = useMutation('merchantOrderStep');
  const [scan, setScan] = useState('');
  const [scanError, setScanError] = useState('');
  const [count, setCount] = useState('');
  const [photos, setPhotos] = useState([]);
  const [courier, setCourier] = useState('');
  const [tracking, setTracking] = useState('');
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [uploading, setUploading] = useState(false);
  const [pickupCourier, setPickupCourier] = useState('');
  const [pickupTracking, setPickupTracking] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [returnScans, setReturnScans] = useState({});
  const [qcNote, setQcNote] = useState('');
  const [uploadError, setUploadError] = useState('');

  const run = async (key, input, message) => {
    try {
      const res = await step.run({ id, step: key, input });
      q.setData((prev) => ({ ...prev, data: res.data }));
      toast.success(message);
      return true;
    } catch {
      return false;
    }
  };

  const confirmPick = async (order) => {
    // Instant feedback against the masks; the server compares the full serial (HMAC).
    // Vendor lines are shipped by their vendor (B17, D79): the store never scans them.
    const pending = (order.items || [order.unit]).filter((u) => !u.picked && !u.vendor);
    const lot = pending.find((u) => skuMatches(scan, u));
    if (!lot && !pending.some((u) => !isLotLine(u) && serialMatches(scan, u))) {
      setScanError(`Scanned ${maskSerial(scan) || 'nothing'} — this order needs ${pending.map((u) => (isLotLine(u) ? u.sku : u.serial_masked)).join(' or ')}`);
      return;
    }
    if (lot && Number(count) !== lot.qty) {
      setScanError(`Count the items: this order needs exactly ${lot.qty} of ${lot.sku}`);
      return;
    }
    setScanError('');
    if (await run('pick', lot ? { scan, count: Number(count) } : { scan }, lot ? 'Items counted and confirmed' : 'Unit confirmed')) {
      setScan('');
      setCount('');
    }
  };

  // Pack evidence: upload each photo against the order, then pack with the media ids.
  const pack = async (order) => {
    setUploadError('');
    setUploading(true);
    try {
      const media = [];
      for (const file of photos) media.push(await uploadFile(file, { ownerType: 'sales_order', ownerId: order.record_id, kind: 'pack' }));
      if (await run('pack', { media_ids: media.map((m) => m.media_id) }, 'Marked packed')) setPhotos([]);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(res) => {
          const o = res.data;
          const steps = o.steps || availableSteps(o);
          const all = o.items && o.items.length ? o.items : [o.unit];
          // The store scans, packs and ships its own lines; vendor lines are the vendor's (B17, D79).
          const units = all.filter((u) => !u.vendor);
          const vendorItems = all.filter((u) => u.vendor);
          const overdue = o.status === 'new' && new Date(o.accept_by).getTime() < Date.now();
          return (
            <>
              <PageHeader
                title={`Order #${o.id}`}
                subtitle={<span className="inline-flex flex-wrap items-center gap-2"><StatusBadge status={o.status} /> placed {when(o.placed_at)}</span>}
                back={{ label: 'Orders', href: '/orders' }}
                actions={steps.includes('exception') ? <Button variant="outline" leftIcon={<AlertOctagon size={16} />} onClick={() => setExceptionOpen(true)}>Raise exception</Button> : null}
              />
              {step.error ? <Alert variant="error" title="Not done">{step.error}</Alert> : null}
              {o.status === 'exception' ? <Alert variant="error" title="Exception">{o.exception_reason}</Alert> : null}
              {(o.requests || []).filter((r) => ['open', 'approved'].includes(r.status)).map((r) => (
                <Alert key={r.id} variant="warning" title={`${r.kind === 'return' ? (r.status === 'approved' ? 'Return approved — awaiting the device' : 'Return requested') : 'Customer request'} · ${r.id} · ${when(r.created_at)}`}>
                  {r.topic ? <strong>{r.topic}: </strong> : null}{r.message}
                </Alert>
              ))}

              <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-4">
                  {steps.includes('accept') ? (
                    <SectionCard title="1. Accept">
                      <p className="text-sm">Accept by <strong>{when(o.accept_by)}</strong>{overdue ? <Badge variant="error" size="sm" className="ml-2">Past cutoff</Badge> : null}. Unaccepted orders are auto-cancelled and refunded.</p>
                      <Button leftIcon={<Check size={16} />} loading={step.loading} onClick={() => run('accept', {}, 'Order accepted')}>Accept order</Button>
                    </SectionCard>
                  ) : null}

                  {o.status === 'accepted' ? (
                    <SectionCard title="2. Pick the exact unit">
                      {o.picked_at ? (
                        <Alert variant="success">{!units.length ? 'Every item ships from its vendor; nothing to pick.' : units.length > 1 ? `All ${units.length} units confirmed ${when(o.picked_at)}.` : `Unit ${units[0].serial_masked} confirmed ${when(o.picked_at)}.`}</Alert>
                      ) : (
                        <>
                          <p className="text-sm">
                            Scan the serial barcode on {units.length > 1 ? 'each unit' : <>unit <span className="font-mono">{o.unit.sku}</span></>}. It must match the serial on the order.
                            {units.some(isLotLine) ? ' For stock sold by quantity, scan its SKU and enter how many you packed.' : ''}
                          </p>
                          <ul className="space-y-1 text-sm">
                            {units.map((u) => (
                              <li key={u.sku} className="flex items-center gap-2">
                                {u.picked ? <Badge variant="success" size="sm">Confirmed</Badge> : <Badge variant="secondary" size="sm">To scan</Badge>}
                                <span className="font-mono">{u.sku}</span> · {isLotLine(u) ? <span>{u.qty} to count</span> : <span className="font-mono">{u.serial_masked}</span>}
                              </li>
                            ))}
                          </ul>
                          <div className="flex flex-wrap items-start gap-2">
                            <div className="flex-1 min-w-[14rem]">
                              <Input
                                placeholder={units.some(isLotLine) ? 'Scan the serial or SKU' : 'Scan or type the serial'}
                                value={scan}
                                onChange={(e) => { setScan(e.target.value); setScanError(''); }}
                                onKeyDown={(e) => e.key === 'Enter' && confirmPick(o)}
                                leftIcon={<ScanLine size={16} />}
                                error={scanError}
                                aria-label="Serial scan" />
                            </div>
                            {units.some((u) => isLotLine(u) && !u.picked) ? (
                              <div className="w-28">
                                <Input type="number" placeholder="Count" value={count} onChange={(e) => { setCount(e.target.value); setScanError(''); }} aria-label="Item count" />
                              </div>
                            ) : null}
                            <Button onClick={() => confirmPick(o)} loading={step.loading} disabled={!scan.trim()}>Confirm</Button>
                          </div>
                        </>
                      )}
                      {o.picked_at ? (
                        <div className="space-y-2">
                          <h3 className="font-medium">3. Pack evidence</h3>
                          {!units.length ? <p className="text-sm opacity-80">Vendors pack and ship their own items; no photos needed.</p> : null}
                          <FileUpload accept="image/*" multiple files={photos} onFileSelect={setPhotos} onFileRemove={(i) => setPhotos(photos.filter((_, k) => k !== i))} />
                          {uploadError ? <Alert variant="error" title="Upload failed">{uploadError}</Alert> : null}
                          <Button leftIcon={<PackageCheck size={16} />} loading={step.loading || uploading} disabled={units.length > 0 && !photos.length} onClick={() => pack(o)}>
                            {units.length ? `Mark packed (${photos.length} photo${photos.length === 1 ? '' : 's'})` : 'Mark packed'}
                          </Button>
                        </div>
                      ) : null}
                    </SectionCard>
                  ) : null}

                  {steps.includes('dispatch') ? (
                    <SectionCard title="4. Dispatch">
                      {vendorItems.some((u) => u.vendor.status === 'to_ship') ? (
                        <Alert variant="warning" title="Waiting for vendors">{vendorItems.filter((u) => u.vendor.status === 'to_ship').length} vendor item(s) not shipped yet. Dispatch opens once every vendor has shipped.</Alert>
                      ) : null}
                      {units.length ? (
                        <>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <Select label="Courier" placeholder="Pick a courier" options={COURIERS.map((c) => ({ value: c, label: c }))} value={courier} onChange={setCourier} closeOnSelect />
                            <Input label="Tracking number" placeholder="BD123456789" value={tracking} onChange={(e) => setTracking(e.target.value)} />
                          </div>
                          <Button leftIcon={<Truck size={16} />} loading={step.loading} disabled={!courier || !tracking.trim()} onClick={() => run('dispatch', { courier, tracking }, 'Dispatched')}>Dispatch</Button>
                        </>
                      ) : (
                        <Button leftIcon={<Truck size={16} />} loading={step.loading} disabled={vendorItems.some((u) => u.vendor.status === 'to_ship')} onClick={() => run('dispatch', {}, 'Marked dispatched')}>Confirm vendors shipped</Button>
                      )}
                    </SectionCard>
                  ) : null}

                  {steps.includes('deliver') ? (
                    <SectionCard title="5. Delivery">
                      {o.courier ? <p className="text-sm">With {o.courier} · <span className="font-mono">{o.tracking}</span></p> : <p className="text-sm">Shipped by the vendor{vendorItems.length > 1 ? 's' : ''}; see the tracking on each item.</p>}
                      <Button variant="success" leftIcon={<CheckCircle2 size={16} />} loading={step.loading} onClick={() => run('deliver', {}, 'Marked delivered')}>Mark delivered</Button>
                    </SectionCard>
                  ) : null}

                  {steps.includes('approve_return') ? (
                    <SectionCard title="Return request">
                      <p className="text-sm">Approve to arrange a reverse pickup (tracking is optional now), or reject with a reason the customer will see.</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Select label="Pickup courier (optional)" placeholder="Pick a courier" options={COURIERS.map((c) => ({ value: c, label: c }))} value={pickupCourier} onChange={setPickupCourier} closeOnSelect />
                        <Input label="Pickup tracking (optional)" placeholder="DL123456789" value={pickupTracking} onChange={(e) => setPickupTracking(e.target.value)} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button leftIcon={<Check size={16} />} loading={step.loading} onClick={() => run('approve_return', { courier: pickupCourier, tracking: pickupTracking }, 'Return approved')}>Approve return</Button>
                        <Button variant="outline" onClick={() => setRejectOpen(true)}>Reject return</Button>
                      </div>
                    </SectionCard>
                  ) : null}

                  {steps.includes('receive_return') ? (
                    <SectionCard title="Receive the return">
                      <p className="text-sm">Scan every unit that came back. A refund is issued only when each serial matches what was sold and the unit passes return QC.</p>
                      <div className="space-y-2">
                        {units.map((u) => (
                          <Input
                            key={u.sku}
                            label={isLotLine(u) ? `${u.sku} · ${u.qty} items` : `${u.sku} · ${u.serial_masked}`}
                            placeholder={isLotLine(u) ? 'Scan the SKU' : 'Scan or type the serial'}
                            value={returnScans[u.sku] || ''}
                            onChange={(e) => setReturnScans((m) => ({ ...m, [u.sku]: e.target.value }))}
                            leftIcon={<ScanLine size={16} />}
                            error={returnScans[u.sku] && !(isLotLine(u) ? skuMatches(returnScans[u.sku], u) : serialMatches(returnScans[u.sku], u)) ? `This is not ${isLotLine(u) ? u.sku : u.serial_masked}` : ''} />
                        ))}
                      </div>
                      <Textarea label="Return QC notes" rows={2} value={qcNote} onChange={(e) => setQcNote(e.target.value)} helperText="Required when the return fails QC; the customer sees it." />
                      <div className="flex flex-wrap gap-2">
                        <Button variant="success" leftIcon={<CheckCircle2 size={16} />} loading={step.loading} disabled={units.some((u) => !serialMatches(returnScans[u.sku] || '', u))}
                          onClick={() => run('receive_return', { result: 'pass', scans: Object.values(returnScans), note: qcNote }, 'Return accepted — refund issued')}>
                          Passed QC — refund
                        </Button>
                        <Button variant="destructive" leftIcon={<AlertOctagon size={16} />} loading={step.loading} disabled={qcNote.trim().length < 5}
                          onClick={() => run('receive_return', { result: 'fail', scans: Object.values(returnScans), note: qcNote }, 'Return marked as failed QC')}>
                          Failed QC
                        </Button>
                      </div>
                    </SectionCard>
                  ) : null}

                  <SectionCard title="Timeline">
                    <ol className="relative border-l border-[var(--app-border)] ml-2 space-y-4">
                      {[...o.timeline].reverse().map((t, i) => (
                        <li key={`${t.at}-${i}`} className="ml-4">
                          <span className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full ${i === 0 ? 'bg-primary-600' : 'bg-secondary-300'}`} aria-hidden />
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge status={t.status} size="sm" />
                            <time className="text-xs opacity-70">{when(t.at)}</time>
                          </div>
                          <p className="text-sm mt-1">{t.note}</p>
                        </li>
                      ))}
                    </ol>
                  </SectionCard>
                </div>

                <aside className="space-y-4">
                  <SectionCard title={all.length > 1 ? `Items (${all.length})` : 'Item'}>
                    {all.length > 1 ? all.map((u) => (
                      <div key={u.sku}>
                        <div className="font-medium">{u.title}</div>
                        <div className="text-sm opacity-70">{u.specs} · <span className="font-mono">{u.sku}</span>{u.vendor ? ` · Qty ${u.qty}` : ` · ${u.serial_masked} · Grade ${u.grade}`}</div>
                        {u.vendor ? <VendorLine v={u.vendor} /> : null}
                      </div>
                    )) : (
                      <>
                        <div className="font-medium">{o.item.title}</div>
                        <div className="text-sm opacity-70">{o.item.specs}</div>
                        {vendorItems[0] ? <VendorLine v={vendorItems[0].vendor} /> : null}
                      </>
                    )}
                    <KeyValue items={[
                      units.length === 1 ? { label: 'Unit SKU', value: <span className="font-mono">{o.unit.sku}</span> } : null,
                      units.length === 1 ? { label: 'Serial', value: <span className="font-mono">{o.unit.serial_masked}</span> } : null,
                      units.length === 1 ? { label: 'Grade', value: o.unit.grade } : null,
                      o.courier ? { label: 'Courier', value: o.courier } : null,
                      o.tracking ? { label: 'Tracking', value: <span className="font-mono">{o.tracking}</span> } : null,
                    ]} />
                  </SectionCard>
                  <SectionCard title="Customer">
                    <KeyValue items={[
                      { label: 'Name', value: o.customer.name },
                      { label: 'Phone', value: o.customer.phone_masked },
                      { label: 'Ship to', value: `${o.customer.area}, ${o.customer.city} ${o.customer.pin}` },
                    ]} />
                  </SectionCard>
                  <SectionCard title="Payment">
                    <KeyValue items={[
                      { label: units.length > 1 ? 'Items' : 'Item', value: <Money paise={o.item_paise} /> },
                      { label: 'Shipping', value: o.shipping_paise ? <Money paise={o.shipping_paise} /> : 'Free' },
                      { label: 'Total', value: <Money paise={o.amount_paise} className="font-bold" /> },
                      { label: 'Method', value: `${o.payment || '—'} · paid` },
                      { label: 'Date', value: formatDate(o.placed_at) },
                    ]} />
                  </SectionCard>
                </aside>
              </div>

              <Dialog
                isOpen={rejectOpen}
                onClose={() => setRejectOpen(false)}
                title="Reject the return"
                footer={
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button variant="destructive" disabled={rejectReason.trim().length < 5} loading={step.loading} onClick={async () => { if (await run('reject_return', { reason: rejectReason }, 'Return rejected')) { setRejectOpen(false); setRejectReason(''); } }}>
                      Reject return
                    </Button>
                  </div>
                }
              >
                <Textarea label="Reason for the customer" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} helperText="Shown on the customer's order. At least 5 characters." />
              </Dialog>

              <Dialog
                isOpen={exceptionOpen}
                onClose={() => setExceptionOpen(false)}
                title="Raise an exception"
                footer={
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setExceptionOpen(false)}>Cancel</Button>
                    <Button variant="destructive" disabled={reason.trim().length < 5} loading={step.loading} onClick={async () => { if (await run('exception', { reason }, 'Exception raised')) { setExceptionOpen(false); setReason(''); } }}>
                      Raise exception
                    </Button>
                  </div>
                }
              >
                <Textarea label="What went wrong?" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} helperText="Visible to platform support. At least 5 characters." />
              </Dialog>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}

/** A vendor line's seller and shipping state (B17): the vendor ships it, the store sees where it is. */
function VendorLine({ v }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      <Badge size="xs" variant="outline">Vendor: {v.name}</Badge>
      <StatusBadge status={v.status === 'to_ship' ? 'pending' : v.status} label={v.status === 'to_ship' ? 'Vendor to ship' : undefined} />
      {v.tracking ? <span className="opacity-80">{v.courier} · <span className="font-mono">{v.tracking}</span></span> : null}
      <span className="opacity-70">Commission {formatINR(v.commission_paise)}</span>
    </div>
  );
}
